// @flow

import { numberWithComma } from 'frontend_core/dist/helpers/format'
import { type State as ReservationDetailState } from 'redux/modules/reservationDetail'
import { type State as ReservationEditState } from 'redux/modules/reservationEdit'
import { isNullOrUndefined } from 'util'
import { type PropTypes as BillStatementProps, type BillItem } from './index'

function getBillItemList(
  reservedValue: number,
  reservedText: string,
  unapprovedValue: number,
  unapprovedText: string,
  name?: string,
): Array<BillItem> {
  const billItemList = []
  if (
    (!isNullOrUndefined(reservedValue) ||
      !isNullOrUndefined(unapprovedValue)) &&
    (reservedValue !== 0 || unapprovedValue !== 0)
  ) {
    if (reservedValue === unapprovedValue || reservedValue === 0) {
      // 同じ時もしくは新たに追加された時
      billItemList.push({
        name,
        price: unapprovedText,
      })
    } else {
      billItemList.push(
        {
          name,
          price: reservedText,
          changed: true,
        },
        {
          name,
          price: unapprovedText,
        },
      )
    }
  }
  return billItemList
}

function getBillStatementProps(reservation, unapproved) {
  const reservedPlan = reservation.plan || {}
  const reservedBilling = reservation.billing || {}

  const unapprovedPlan = unapproved.plan || {}
  const unapprovedBilling = unapproved.billing || {}

  const planBillItemList = [
    {
      name: unapprovedPlan.name,
      price: unapprovedBilling.room_amount_text,
    },
  ]
  if (
    reservedPlan.name !== unapprovedPlan.name ||
    reservedBilling.room_amount_text !== unapprovedBilling.room_amount_text
  ) {
    planBillItemList.unshift({
      name: reservedPlan.name,
      price: reservedBilling.room_amount_text,
      changed: true,
    })
  }

  const reservedOptionItems = reservation.reserved_option_items || []
  let unapprovedOptionItems = unapproved.reserved_option_items || []
  const optionItems = []
  reservedOptionItems.forEach(reservedOption => {
    const foundOption = unapprovedOptionItems.find(
      unapprovedOption =>
        unapprovedOption.option_item_id === reservedOption.option_item_id,
    )
    if (foundOption) {
      unapprovedOptionItems = unapprovedOptionItems.filter(
        unapprovedOption =>
          unapprovedOption.option_item_id !== foundOption.option_item_id,
      )
    }

    const isChanged =
      !!foundOption && reservedOption.number !== foundOption.number
    optionItems.push({
      name: reservedOption.name,
      price: reservedOption.total_amount_text,
      number: reservedOption.countable && reservedOption.number,
      removed: foundOption === undefined,
      changed: isChanged,
    })
    if (isChanged) {
      optionItems.push({
        name: foundOption.name,
        price: foundOption.total_amount_text,
        number: foundOption.countable && foundOption.number,
      })
    }
  })
  const temp = unapprovedOptionItems.forEach(unapprovedOption => {
    optionItems.push({
      name: unapprovedOption.name,
      price: unapprovedOption.total_amount_text,
      number: unapprovedOption.countable && unapprovedOption.number,
      added: true,
    })
  })

  const additionalFee =
    parseInt(unapprovedBilling.total_pay_amount_for_user_with_tax) -
    parseInt(reservedBilling.total_pay_amount_for_user_with_tax)

  const additionalPaidyAmount = parseInt(
    unapprovedBilling.additional_paidy_charge_amount_for_change,
  )

  return {
    prevBill: {
      tax: reservedBilling.guest_sales_tax_text,
    },
    nextBill: {
      tax: unapprovedBilling.guest_sales_tax_text,
    },

    planBillItemList,
    extraGuestStayBillItemList: getBillItemList(
      reservedBilling.extra_guest_stay_amount,
      reservedBilling.extra_guest_stay_amount_text,
      unapprovedBilling.extra_guest_stay_amount,
      unapprovedBilling.extra_guest_stay_amount_text,
    ),
    optionCleaningBillItemList: getBillItemList(
      reservedBilling.option_cleaning_amount,
      reservedBilling.option_cleaning_amount_text,
      unapprovedBilling.option_cleaning_amount,
      unapprovedBilling.option_cleaning_amount_text,
    ),
    optionBillItemList: optionItems,
    changePriceBillItemList: getBillItemList(
      parseInt(reservedBilling.change_amount, 10),
      reservedBilling.change_amount_text,
      parseInt(unapprovedBilling.change_amount, 10),
      unapprovedBilling.change_amount_text,
      unapprovedBilling.change_description,
    ),
    lastMinuteDiscountBillItemList: getBillItemList(
      reservedBilling.last_minute_discount_amount_except_tax,
      `-${reservedBilling.last_minute_discount_amount_except_tax_text}`,
      unapprovedBilling.last_minute_discount_amount_except_tax,
      `-${unapprovedBilling.last_minute_discount_amount_except_tax_text}`,
    ),
    weeklyDiscountBillItemList: getBillItemList(
      reservedBilling.weekly_discount_amount,
      `-${reservedBilling.weekly_discount_amount_text}`,
      unapprovedBilling.weekly_discount_amount,
      `-${unapprovedBilling.weekly_discount_amount_text}`,
    ),
    monthlyDiscountBillItemList: getBillItemList(
      reservedBilling.monthly_discount_amount,
      `-${reservedBilling.monthly_discount_amount_text}`,
      unapprovedBilling.monthly_discount_amount,
      `-${unapprovedBilling.monthly_discount_amount_text}`,
    ),
    guestFeeBillItemList: getBillItemList(
      reservedBilling.guest_fee_amount,
      reservedBilling.guest_fee_amount_text,
      unapprovedBilling.guest_fee_amount,
      unapprovedBilling.guest_fee_amount_text,
    ),
    pointDiscountBillItemList: getBillItemList(
      reservedBilling.point_discount_amount,
      `-${reservedBilling.point_discount_amount_text}`,
      unapprovedBilling.point_discount_amount,
      `-${unapprovedBilling.point_discount_amount_text}`,
    ),
    guestPostpayFeeBillItemList: getBillItemList(
      reservedBilling.guest_postpay_fee_amount,
      reservedBilling.guest_postpay_fee_amount_text,
      unapprovedBilling.guest_postpay_fee_amount,
      unapprovedBilling.guest_postpay_fee_amount_text,
    ),
    salesTaxBillItemList: getBillItemList(
      reservedBilling.guest_sales_tax,
      reservedBilling.guest_sales_tax_text,
      unapprovedBilling.guest_sales_tax,
      unapprovedBilling.guest_sales_tax_text,
    ),
    couponDiscountBillItemList: getBillItemList(
      reservedBilling.discount_amount,
      reservedBilling.discount_amount_text,
      unapprovedBilling.discount_amount,
      unapprovedBilling.discount_amount_text,
    ),
    reservedTotal: reservedBilling.total_pay_amount_for_user_with_tax_text,
    unapprovedTotal: unapprovedBilling.total_pay_amount_for_user_with_tax_text,
    additionalFee:
      additionalFee > 0 ? `￥${numberWithComma(additionalFee)}` : undefined,
    additionalPaidyAmount:
      additionalPaidyAmount > 0
        ? `￥${numberWithComma(additionalPaidyAmount)}`
        : undefined,
  }
}

export function getBillStatementPropsForEdit(
  reservationDetail: ReservationDetailState,
  reservationEdit: ReservationEditState,
): BillStatementProps {
  const { detail } = reservationDetail
  const unapproved = reservationEdit.unapprovedReservation
  return getBillStatementProps(detail, unapproved)
}

export function getBillStatementPropsForChange(
  reservation: ReservationType,
  unapprovedReservation: ReservationType,
): BillStatementProps {
  return getBillStatementProps(reservation, unapprovedReservation)
}



// WEBPACK FOOTER //
// ./src/components/molecules/BillStatement/presenter.js